/* eslint-disable no-console */
/**
 * Logger utility that respects environment settings
 *
 * @description Provides environment-aware logging with different log levels:
 * - Debug logs only appear in development
 * - Info logs appear in development and staging
 * - Warning and error logs always appear
 *
 * @example
 * ```typescript
 * import { logger } from '@utils/logger';
 *
 * // Debug info (dev only)
 * logger.debug('User data loaded', userData);
 * logger.debug('Searching for products', searchTerm);
 *
 * // Info (dev + staging)
 * logger.info('Payment processed successfully');
 *
 * // Warnings and errors (always shown)
 * logger.warn('Deprecated API used');
 * logger.error('Failed to fetch data', error);
 *
 * // Performance timing (dev only)
 * logger.time('api-call');
 * // ... some async operation
 * logger.timeEnd('api-call');
 *
 * // Grouped logs (dev only)
 * logger.group('User Registration', () => {
 *   logger.debug('Validating email...');
 *   logger.debug('Creating user record...');
 * });
 * ```
 */

import { isProductionEnvironment, isTestEnvironment } from './environment';

const isProduction = isProductionEnvironment();
const isTest = isTestEnvironment();

export const logger = {
  /**
   * Debug logs - only shown in development
   * @param message - The debug message to log
   * @param args - Additional arguments to log
   */
  debug: (message: string, ...args: any[]) => {
    if (!isTest) {
      console.debug(message, ...args);
    }
  },

  /**
   * Info logs - shown in development and staging
   * @param message - The info message to log
   * @param args - Additional arguments to log
   */
  info: (message: string, ...args: any[]) => {
    if (!isProduction) {
      console.info(message, ...args);
    }
  },

  /**
   * Warning logs - always shown
   * @param message - The warning message to log
   * @param args - Additional arguments to log
   */
  warn: (message: string, ...args: any[]) => {
    if (!isTest) {
      console.warn(message, ...args);
    }
  },

  /**
   * Error logs - always shown
   * @param message - The error message to log
   * @param args - Additional arguments to log
   */
  error: (message: string, ...args: any[]) => {
    if (!isTest) {
      console.error(message, ...args);
    }
  },

  /**
   * Start performance timing (only in development)
   * @param label - The label for the timer
   */
  time: (label: string) => {
    if (!isTest) {
      console.time(label);
    }
  },

  /**
   * End performance timing (only in development)
   * @param label - The label for the timer
   */
  timeEnd: (label: string) => {
    if (!isTest) {
      console.timeEnd(label);
    }
  }
};
